import { useEffect, useState } from 'react';

import useTranslation from '../../utils/useTranslation';
import aboutContent from '../../data/aboutContent';
import SectionHeader from '../SectionHeader';
import Reveal from '../Reveal';
import OrgChartDiagram from '../OrgChartDiagram';

const VisionIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const MissionIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <circle cx="12" cy="12" r="6" />
    <circle cx="12" cy="12" r="2" />
  </svg>
);

const GrowthIcon = () => (
  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="22 7 13.5 15.5 8.5 10.5 2 17" />
    <polyline points="16 7 22 7 22 13" />
  </svg>
);

const HandshakeIcon = () => (
  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <path d="m11 17 2 2a1 1 0 1 0 3-3" />
    <path d="m14 14 2.5 2.5a1 1 0 1 0 3-3l-3.88-3.88a3 3 0 0 0-4.24 0l-.88.88a1 1 0 1 1-3-3l2.81-2.81a5.79 5.79 0 0 1 7.06-.87l.47.28a2 2 0 0 0 1.42.25L21 4" />
    <path d="m21 3 1 11h-2" />
    <path d="M3 3 2 14l6.5 6.5a1 1 0 1 0 3-3" />
    <path d="M3 4h8" />
  </svg>
);

const PeopleIcon = () => (
  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const BuildingIcon = () => (
  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z" />
    <path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2" />
    <path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2" />
    <path d="M10 6h4M10 10h4M10 14h4M10 18h4" />
  </svg>
);

// Distinct icon per strategic objective (kept in content order).
const GOAL_ICONS = [BuildingIcon, PeopleIcon, HandshakeIcon, GrowthIcon];

// Decorative footer line-art for the vision / mission cards.
const EnvelopeDeco = () => (
  <svg width="58" height="34" viewBox="0 0 58 34" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <rect x="23" y="8" width="31" height="20" rx="2.5" />
    <path d="M24 11 38.5 21 53 11" />
    <path d="M14 10H4M12 18H2M15 26H6" />
  </svg>
);

const MountainFlagDeco = () => (
  <svg width="58" height="34" viewBox="0 0 58 34" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <path d="M7 29h44" />
    <path d="M13 29 25 9l8 12" />
    <path d="M29 29 39 15l11 14" />
    <path d="M25 9V3l7 2-7 2" />
  </svg>
);

const DeptIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z" />
    <path d="M10 6h4M10 10h4M10 14h4M10 18h4" />
  </svg>
);

function OrgSectionContent({ blocks, variant }) {
  return blocks.map((b, j) => {
    if (variant === 'departments' && b.type === 'ul') {
      return (
        <ul key={j} className="ab-org-dept-grid">
          {b.items.map((item, k) => (
            <li key={k} className="ab-org-dept-card">
              <span className="ab-org-dept-icon" aria-hidden="true">
                <DeptIcon />
              </span>
              <span className="ab-org-dept-label">{item}</span>
            </li>
          ))}
        </ul>
      );
    }

    if (b.type === 'ul') {
      return (
        <ul key={j} className="ab-org-list">
          {b.items.map((item, k) => (
            <li key={k}>{item}</li>
          ))}
        </ul>
      );
    }

    return <Block key={j} block={b} />;
  });
}

function OrgStructureBlock({ banner, sections }) {
  return (
    <div className="ab-org-structure">
      {banner?.chart && (
        <figure className="ab-org-banner">
          <OrgChartDiagram chart={banner.chart} ariaLabel={banner.alt || ''} />
        </figure>
      )}

      <div className="ab-org-sections">
        {sections.map((section, i) => (
          <section key={i} className="ab-org-section">
            <div className="ab-org-section-body">
              <h3 className="ab-org-section-title">{section.title}</h3>
              <div className="ab-org-section-content">
                <OrgSectionContent blocks={section.blocks} variant={section.variant} />
              </div>
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}

export function Block({ block }) {
  const { t } = useTranslation();

  switch (block.type) {
    case 'lead':
      return <p className="about-lead">{block.text}</p>;
    case 'banner':
      return (
        <figure className="about-banner">
          <img
            src={`${process.env.PUBLIC_URL}/${block.src}`}
            alt={block.alt || ''}
            loading="lazy"
          />
        </figure>
      );
    case 'tagline':
      return <p className="about-tagline">{block.text}</p>;
    case 'h':
      return <h3 className="about-block-title">{block.text}</h3>;
    case 'ul':
      return (
        <ul className="about-list">
          {block.items.map((it, i) => (
            <li key={i}>{it}</li>
          ))}
        </ul>
      );
    case 'links':
      return (
        <ul className="about-doc-links">
          {block.items.map((lnk, i) => (
            <li key={i}>
              <a className="about-doc-link" href={lnk.href}>
                <span className="about-doc-link-icon" aria-hidden="true">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.75"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
                    <path d="M14 3v5h5" />
                    <path d="M8 13h8" />
                    <path d="M8 17h5" />
                  </svg>
                </span>
                <span className="about-doc-link-label">{lnk.label}</span>
                <span className="about-doc-link-type">PDF</span>
              </a>
            </li>
          ))}
        </ul>
      );
    case 'org-structure':
      return <OrgStructureBlock banner={block.banner} sections={block.sections} />;
    case 'chambers':
      return (
        <>
          {block.subtitle && <p className="about-chambers-intro">{block.subtitle}</p>}
          <div className="ab-chambers">
            {block.items.map((c, i) => (
              <a
                key={i}
                className="ab-chamber-card"
                href={c.href}
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  className="ab-chamber-logo"
                  src={`${process.env.PUBLIC_URL}/${c.src}`}
                  alt={c.name}
                  loading="lazy"
                />
                <h4 className="ab-chamber-name">{c.name}</h4>
                <p className="ab-chamber-location">
                  <svg
                    viewBox="0 0 24 24"
                    width="14"
                    height="14"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M12 21s7-4.5 7-10a7 7 0 1 0-14 0c0 5.5 7 10 7 10z" />
                    <circle cx="12" cy="11" r="2.5" />
                  </svg>
                  {c.location}
                </p>
                <span className="ab-chamber-cta">
                  {t('view_details')}
                  <svg
                    className="ab-chamber-cta-arrow"
                    viewBox="0 0 24 24"
                    width="16"
                    height="16"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <line x1="5" y1="12" x2="19" y2="12" />
                    <polyline points="12 5 19 12 12 19" />
                  </svg>
                </span>
              </a>
            ))}
          </div>
        </>
      );
    case 'stats':
      return (
        <div className="about-stats">
          {block.items.map((s, i) => (
            <div className="about-stat" key={i}>
              <span className="about-stat-value">{s.value}</span>
              <span className="about-stat-label">{s.label}</span>
            </div>
          ))}
        </div>
      );
    case 'signature':
      return (
        <div className="about-signature">
          <span className="about-signature-name">{block.name}</span>
          <span className="about-signature-role">{block.role}</span>
        </div>
      );
    case 'vision-mission':
      return (
        <div className="ab-vm-grid">
          {block.items.map((item, i) => {
            const isVision = item.icon === 'vision';
            return (
              <div
                key={i}
                className={`ab-vm-card ${isVision ? 'ab-vm-card--vision' : 'ab-vm-card--mission'}`}
              >
                <span className="ab-vm-icon" aria-hidden="true">
                  {isVision ? <VisionIcon /> : <MissionIcon />}
                </span>
                <h4 className="ab-vm-title">{item.label}</h4>
                <p className="ab-vm-text">{item.text}</p>
                <span className="ab-vm-deco" aria-hidden="true">
                  {isVision ? <MountainFlagDeco /> : <EnvelopeDeco />}
                </span>
              </div>
            );
          })}
        </div>
      );
    case 'goals-grid':
      return (
        <div className="ab-goals-grid">
          {block.items.map((text, i) => {
            const Icon = GOAL_ICONS[i % GOAL_ICONS.length];
            return (
              <div key={i} className="ab-goal">
                <span className="ab-goal-ico" aria-hidden="true">
                  <Icon />
                </span>
                <p className="ab-goal-text">{text}</p>
              </div>
            );
          })}
        </div>
      );
    case 'tasks-timeline':
      return (
        <ol
          className="ab-tasks"
          style={{ '--ab-task-rows': Math.ceil(block.items.length / 2) }}
        >
          {block.items.map((text, i) => (
            <li key={i} className="ab-task">
              <span className="ab-task-num" aria-hidden="true">{String(i + 1).padStart(2, '0')}</span>
              <p className="ab-task-text">{text}</p>
            </li>
          ))}
        </ol>
      );
    case 'p':
    default:
      return <p className="about-body">{block.text}</p>;
  }
}

export default function AboutSection({ innerRef, requestedTab }) {
  const { t, lang } = useTranslation();
  const tabs = aboutContent[lang] || aboutContent.ar;
  const [activeId, setActiveId] = useState(tabs[0]?.id);

  // When the navbar dropdown requests a specific tab, switch to it. `key`
  // bumps on every click so re-selecting the same tab still works.
  useEffect(() => {
    if (requestedTab?.id && tabs.some((tab) => tab.id === requestedTab.id)) {
      setActiveId(requestedTab.id);
    }
  }, [requestedTab, tabs]);

  const active = tabs.find((tab) => tab.id === activeId) || tabs[0];

  return (
    <section className="section alt" id="about" ref={innerRef}>
      <div className="container">
        <SectionHeader icon="about" title={t('about_title')} />

        <Reveal className="about-tabs">
          <aside className="about-tablist" role="tablist" aria-label={t('about_title')}>
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={tab.id === active.id}
                className={`about-tab${tab.id === active.id ? ' is-active' : ''}`}
                onClick={() => setActiveId(tab.id)}
              >
                <span className="about-tab-chevron" aria-hidden="true">
                  {'\u203A'}
                </span>
                <span>{tab.title}</span>
              </button>
            ))}
          </aside>

          <div className="about-panel" role="tabpanel">
            <h3 className="about-panel-title">{active.title}</h3>
            {active.blocks.map((block, i) => (
              <Block key={i} block={block} />
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
